import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * Builds a short, honest usage note to bundle alongside the font binary.
 * This is a courtesy readme, not a license — we don't know the real
 * license terms of an arbitrary font file, so we say that plainly rather
 * than inventing legal language.
 */
function buildReadme(font) {
  return `${font.name}
${'='.repeat(font.name.length)}

File:    ${font.filename}
Family:  ${font.family}
Format:  ${font.format.toUpperCase()}

This archive was generated by Specimen, a local font previewer, for your
convenience. It does not include or imply any license to use this font.

Before using this font in a personal or commercial project, check the
license that came with the original font file or the foundry/distributor
you obtained it from — font licenses vary widely (some are free for any
use, others restrict commercial or web embedding use).

Generated on ${new Date().toISOString().slice(0, 10)}.
`;
}

/**
 * Fetches the binary font asset, packages it with a README.txt into a
 * .zip, and triggers a client-side download. Every async step is guarded
 * so a network hiccup surfaces a clear error instead of a silent failure.
 *
 * @param {{ name: string, family: string, format: string, url: string, filename: string }} font
 * @returns {Promise<void>}
 */
export async function downloadFontAsZip(font) {
  if (!font?.url) {
    throw new Error('This font is missing a download URL.');
  }

  let response;
  try {
    response = await fetch(font.url);
  } catch (networkErr) {
    throw new Error(
      `Could not reach the server to download "${font.name}". Check your connection and try again.`
    );
  }

  if (!response.ok) {
    throw new Error(
      `Server responded with ${response.status} while fetching "${font.name}".`
    );
  }

  let arrayBuffer;
  try {
    arrayBuffer = await response.arrayBuffer();
  } catch (readErr) {
    throw new Error(`Failed to read the font data for "${font.name}".`);
  }

  try {
    const zip = new JSZip();
    zip.file(font.filename, arrayBuffer);
    zip.file('README.txt', buildReadme(font));

    const blob = await zip.generateAsync({ type: 'blob' });
    const zipName = `${font.family || font.name}.zip`.replace(/\s+/g, '-');
    saveAs(blob, zipName);
  } catch (zipErr) {
    console.error('Zip generation failed:', zipErr);
    throw new Error(`Could not package "${font.name}" for download.`);
  }
}
